import { useTranslation } from 'react-i18next';

import { GEN_PREVIEW_MAX, type TermGenResult } from '../termGenerator';

const CZ_DOW_SHORT = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];

function fmtCz(date: Date): string {
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(date.getDate())}. ${p2(date.getMonth() + 1)}. ${date.getFullYear()}`;
}

interface TermPreviewTableProps {
  result: TermGenResult;
  nights: number;
}

/**
 * Živý náhled vygenerovaných termínů. Nad {@link GEN_PREVIEW_MAX} řádků se prostředek sbalí na
 * „… N dalších …" a vykreslí se poslední termín, aby náhled zůstal svižný i u tisíců termínů.
 */
export function TermPreviewTable({ result, nights }: TermPreviewTableProps) {
  const { t } = useTranslation('planner');
  const terms = result.terms;

  if (terms.length === 0) {
    return (
      <div className="text-phx-text-muted text-phx-sm px-4 py-8 text-center leading-relaxed">
        {t('serieDetail.generateTerms.preview.empty')}
      </div>
    );
  }

  const head = terms.slice(0, GEN_PREVIEW_MAX);
  const collapsed = terms.length > GEN_PREVIEW_MAX;
  const hiddenCount = collapsed ? terms.length - GEN_PREVIEW_MAX - 1 : 0;

  const renderRow = (term: { start: Date; end: Date }, key: number) => (
    <tr key={key} className="border-phx-border border-b">
      <td className="text-phx-text-muted px-phx-3 py-1">{CZ_DOW_SHORT[term.start.getDay()]}</td>
      <td className="px-phx-3 py-1 tabular-nums">{fmtCz(term.start)}</td>
      <td className="px-phx-3 py-1 tabular-nums">{fmtCz(term.end)}</td>
      <td className="px-phx-3 py-1 text-right tabular-nums">{nights}</td>
    </tr>
  );

  return (
    <table className="text-phx-sm w-full border-collapse [&>tbody>tr:nth-child(even)]:bg-[rgba(40,30,20,0.02)]">
      <thead>
        <tr className="bg-phx-surface-3 text-phx-text-2 sticky top-0">
          <th className="px-phx-3 py-1 text-left font-semibold">
            {t('serieDetail.generateTerms.preview.day')}
          </th>
          <th className="px-phx-3 py-1 text-left font-semibold">
            {t('serieDetail.generateTerms.preview.start')}
          </th>
          <th className="px-phx-3 py-1 text-left font-semibold">
            {t('serieDetail.generateTerms.preview.end')}
          </th>
          <th className="px-phx-3 py-1 text-right font-semibold">
            {t('serieDetail.generateTerms.preview.nights')}
          </th>
        </tr>
      </thead>
      <tbody>
        {head.map((term, index) => renderRow(term, index))}
        {collapsed ? (
          <>
            <tr className="text-phx-text-faint border-phx-border border-b italic">
              <td colSpan={4} className="px-phx-3 py-1 text-center">
                {t('serieDetail.generateTerms.preview.more', { count: hiddenCount })}
              </td>
            </tr>
            {renderRow(terms[terms.length - 1], -1)}
          </>
        ) : null}
      </tbody>
    </table>
  );
}
